'use client';
import React, { useEffect, useState } from 'react';
import CardWrapper from '../shared/pictureCard/cardWrapper';
import ImageComponentScroll from './imageComponentScroll';
import ImageComponentAction from './imageComponentAction';
import ImageComponentDescription from './imageComponentDescription';
import { Expand } from 'lucide-react';
import { getLocalizedPath } from '@/components/shared/formatURL';
import { useFullScreenModal } from './full-screen-modal';
import Link from 'next/link';
import Image from 'next/image';
import { useReportModal } from './reportModal';
import { useRouter } from 'next/navigation';
import { siteConfigurations } from '../shared/sites/sites';
import UpscaleWrapper from '../upscale/upscaleWrapper';

interface UserData {
  username: string;
  userId: string;
}

const subUpscaleUrl = (post: any): any => {
  if (post?.displayedUpscaleUrl) {
    return post.displayedUpscaleUrl;
  }
  if (post?.upscaleFactor && post.upscaleFactor > 0 && post?.upscaleUrl) {
    return post.upscaleUrl;
  }
  // Default to original image
  return post?.imageUrl || '';
};

const getExpandUrl = (type: string, post: any) => {
  if (type === 'image') {
    return subUpscaleUrl(post);
  } else {
    return post?.videoUrl;
  }
};

export default function ImageComponent({
  action,
  _id,
  type = 'image',
  translations,
  modalTranslations,
  reportModalTranslations,
  shareModalTranslations,
  signInModalTranslations,
  upscaleTranslations,
  downloadModalTranslations
}: {
  action: any;
  _id: string;
  type?: 'image' | 'gif';
  translations: any;
  modalTranslations: any;
  reportModalTranslations: any;
  shareModalTranslations: any;
  signInModalTranslations: any;
  upscaleTranslations: any;
  downloadModalTranslations: any;
}) {
  const [userData, setUserData] = useState<UserData>({ username: '', userId: '' });
  const { FullScreenModal, setFullScreenModal } = useFullScreenModal(
    type,
    getExpandUrl(type, action.post)
  );
  const { ReportModal } = useReportModal(reportModalTranslations);

  const localizedPostSearchPath = getLocalizedPath('post/search');
  const [views, setViews] = useState(action.post?.views);

  const router = useRouter();
  const [relatedPostResponse, setRelatedPostResponse] = useState([
    {
      _id: '',
      author: '',
      createdAt: '',
      imageUrl: '',
      videoUrl: '',
      thumbnailUrl: '',
      likes: 0,
      model: '',
      tagGroups: [],
      updatedAt: '',
      userLiked: false,
      views: 0,
      displayedUpscaleUrl: '',
      displayedUpscaleFactor: 0
    }
  ]);

  useEffect(() => {
    const fetchUsername = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
        if (!backendUrl) return;

        const response = await fetch(`${backendUrl}/post/username/${_id}`);
        if (!response.ok) {
          return; // Will use default empty strings
        }
        const data = await response.json();
        setUserData({
          username: data.username || '',
          userId: data.userId || ''
        });
      } catch (error) {
        // Will use default empty strings
        console.error('Error fetching username:', error);
      }
    };

    fetchUsername();
  }, [_id]);

  useEffect(() => {
    const fetchPostViewData = async () => {
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
      if (!backendUrl) {
        return;
      }

      // Retrieve the viewed posts from localStorage
      const viewedPosts = JSON.parse(localStorage.getItem('viewedPosts') || '[]');

      // Check if the current post ID is already in the viewedPosts array
      if (viewedPosts.includes(_id)) {
        return; // If already viewed, exit early and do not increment the view count
      }

      try {
        const res = await fetch(`${backendUrl}/post/view/${_id}`);
        const json = await res.json();

        if (json.views !== undefined && json.views >= 0) {
          setViews(json.views);

          // Add the post ID to the beginning of the viewedPosts array
          viewedPosts.unshift(_id);

          // Ensure only the last 10 posts are stored
          if (viewedPosts.length > 10) {
            viewedPosts.pop(); // Remove the oldest post (last element in the array)
          }

          // Save the updated viewedPosts array back to localStorage
          localStorage.setItem('viewedPosts', JSON.stringify(viewedPosts));
        } else if (json.message !== undefined && json.message == 'Post not found') {
          // Redirect to the home page or dashboard
          window.location.href = `${process.env.NEXT_PUBLIC_BASE_URL}`;
        }
      } catch (error) {
        // Redirect to the home page or dashboard
        window.location.href = `${process.env.NEXT_PUBLIC_BASE_URL}`;
      }
    };

    fetchPostViewData();
  }, [_id]); // Dependency array with _id ensures fetch runs only if _id changes

  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        const url = `${process.env.NEXT_PUBLIC_BACKEND_URL}/post/related/${_id}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'x-origin': process.env.NEXT_PUBLIC_BASE_URL || ''
          }
        });

        if (!response.ok) {
          router.push('/');
        }

        const data = await response.json();
        setRelatedPostResponse(data);
      } catch (error) {
        router.push('/');
      }
    };

    fetchRelatedPosts();
  }, [_id]);

  return (
    <>
      <FullScreenModal />
      <ReportModal />
      <div className="relative mt-[-23px] grid w-full grid-cols-1 px-0 sm:mt-0 sm:px-5 md:grid-cols-6">
        <div className="col-span-1 my-0 mr-0 block h-[60vh] overflow-hidden border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] shadow-lg sm:h-[70vh] sm:rounded-md md:col-span-4 md:mr-4">
          <div className="relative flex h-full w-full items-center justify-center">
            {type === 'gif' ? (
              <video
                className="h-full w-full rounded-md object-contain"
                src={action.post?.videoUrl}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <UpscaleWrapper
                upscaleFactor={action.post?.displayedUpscaleFactor || 0}
                className="h-full w-full"
              >
                <Image
                  key={subUpscaleUrl(action.post)} // Force re-render when URL changes
                  className="h-full w-full rounded-md object-cover"
                  src={subUpscaleUrl(action.post)}
                  alt={action.tagStringProp}
                  layout="fill"
                  objectFit="contain"
                  loading="eager"
                  priority={true}
                  unoptimized
                />
              </UpscaleWrapper>
            )}
            <button
              aria-label="expand image"
              onClick={() => setFullScreenModal(true)}
              className="absolute bottom-0 right-0"
            >
              <Image
                src={'https://cdn6.imgpog.com/rectangle.svg'}
                alt="expand"
                width={40}
                height={40}
              />
              <Expand color="#2B2D31" className="absolute bottom-0 right-0 h-5 w-auto pb-1 pr-1" />
            </button>
          </div>
        </div>
        <div className="col-span-1 mt-4 px-0 md:col-span-2 md:mt-0">
          <div className="col-span-1 mt-4 border-[0.5px] border-b-0 border-[#3C3C3C] bg-[#2B2D31] pt-4 shadow-md sm:rounded-t-md md:col-span-2 md:mt-0 md:px-5">
            <h1 className="mb-2 px-5 font-bold text-white md:px-0">{action.tagStringProp}</h1>
            <div className="my-auto ml-auto flex flex-row flex-wrap overflow-x-auto px-5 pb-4 md:px-0">
              {userData.username === '' ? (
                <div className="mr-1 text-xs text-white">Anonymous •</div>
              ) : (
                <a
                  className="mr-1 text-xs text-white"
                  href={
                    process.env.NEXT_PUBLIC_BASE_URL + translations.path + 'user/' + userData.userId
                  }
                >
                  {userData.username} •
                </a>
              )}

              <h2 className="mr-1 text-xs text-white"> {views}</h2>
              <h2 className="mr-1 text-xs text-white">{translations.views}</h2>
              <h2 className="mr-1 text-xs text-white">
                • {action.post?.likes || 0} {translations.saves}
              </h2>
              <h2 className="mr-1 text-xs text-white">
                • {type === 'gif' ? translations.AIPornGif : translations.AIPornPicture}
              </h2>
              {action.post?.aspectRatio && (
                <h2 className="mr-1 text-xs text-white">
                  • {translations.aspectRatio}: {action.post?.aspectRatio}
                </h2>
              )}
            </div>
          </div>
          <ImageComponentAction
            action={action}
            _id={_id}
            type={type}
            modalTranslations={modalTranslations}
            translations={translations}
            reportModalTranslations={reportModalTranslations}
            shareModalTranslations={shareModalTranslations}
            signInModalTranslations={signInModalTranslations}
            upscaleTranslations={upscaleTranslations}
            downloadModalTranslations={downloadModalTranslations}
          />
          <ImageComponentDescription
            type={type}
            action={action}
            translations={translations}
            signInModalTranslations={signInModalTranslations}
          />
        </div>
      </div>
      {type !== 'gif' && (
        <>
          {(process.env.NEXT_PUBLIC_BASE_URL === 'http://localhost:3000' ||
          process.env.NEXT_PUBLIC_BASE_URL === 'https://www.8porn.ai'
            ? true
            : siteConfigurations[action.post?.origin]?.siteUrl ===
              process.env.NEXT_PUBLIC_BASE_URL) && (
            <>
              <div className="my-6 flex flex-row px-5">
                <h2 className="relative text-xl font-bold text-white">
                  {translations.SimilarAIPorn}
                </h2>
                <Link
                  prefetch={false}
                  href={localizedPostSearchPath + '?search=' + encodeURIComponent(action.randomTag)}
                  className={`ml-auto flex flex-row items-center justify-center rounded-full p-1.5 text-sm font-bold text-[#6CB4F4] backdrop-blur-xl hover:brightness-105`}
                >
                  {translations.MoreResults}
                </Link>
              </div>

              <div className="w-full px-0 sm:px-2">
                {relatedPostResponse.length > 0 ? (
                  <CardWrapper
                    data={relatedPostResponse}
                    contentType={type}
                    modalTranslations={modalTranslations}
                    signInModalTranslations={signInModalTranslations}
                  />
                ) : (
                  <p className="text-md font-bold text-white">{translations.NoRelatedPostsFound}</p>
                )}
              </div>
            </>
          )}
        </>
      )}
      <ImageComponentScroll />
    </>
  );
}
